import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";

import { Show, TextSkeleton } from "shared/components";
import { IconAI } from "shared/components/ai/IconAI";
import { Card } from "shared/components/Card";
import addressApi from "api/landlord/address";
import { RecommendationSummary } from "api/recommendation-summary/types";
import { useAiRecommendationSummary } from "shared/hooks/use-ai-recommendation-summary";
import { DecisionAction } from "shared/types";
import { Application } from "types/application";
import { AiSummaryFeedback } from "./AiSummaryFeedback";
import { TurnOffAiSummaryDialog } from "./TurnOffAiSummaryDialog";
import { useTypewriter } from "./use-typewriter";

// Generation runs in a background job right after the recommendation is
// evaluated, so the text is normally already there when the modal opens. When
// it is not, poll the detail endpoint (the summary is merged outside its
// cache) while the job can still finish. Its 4 attempts (30s ai-mainframe
// timeout each, plus Sidekiq backoff) are spent within ~5 minutes of the row
// being created, and the backend never re-enqueues a failed one, so a summary
// still empty after that will stay empty.
const PENDING_POLL_INTERVAL_MS = 3_000;
const GENERATION_WINDOW_MS = 5 * 60_000;

const getRemainingGenerationMs = (createdAt: string) =>
  new Date(createdAt).getTime() + GENERATION_WINDOW_MS - Date.now();

interface AiSummaryProps {
  application: Application;
  recommendation: DecisionAction;
}

const SummarySkeleton = () => (
  <div className="space-y-2">
    <TextSkeleton size="sm" />
    <TextSkeleton size="sm" className="w-3/4" />
  </div>
);

const SummaryText = ({ summary }: { summary: RecommendationSummary }) => {
  const { text, isTyping } = useTypewriter(
    summary.summary ?? "",
    String(summary.id)
  );

  return (
    <p className="text-sm font-medium text-content-1000">
      {text}
      <Show if={isTyping}>
        <span
          aria-hidden
          className="inline-block w-0.5 h-[1em] ml-0.5 align-[-0.15em] bg-content-1000 animate-pulse"
        />
      </Show>
    </p>
  );
};

const usePendingSummaryPolling = (application: Application) => {
  const summary = application.recommendation_summary;
  const isPending = summary?.summary === null;
  const createdAt = summary?.created_at ?? "";
  // Decided on mount for an already-failed summary, so it never flashes a
  // skeleton first.
  const [hasTimedOut, setHasTimedOut] = useState(
    () => isPending && getRemainingGenerationMs(createdAt) <= 0
  );

  useEffect(() => {
    if (!isPending) return;
    const timeout = window.setTimeout(
      () => setHasTimedOut(true),
      Math.max(getRemainingGenerationMs(createdAt), 0)
    );
    return () => window.clearTimeout(timeout);
  }, [isPending, createdAt]);

  // Same key as the application page, so the refetch updates the application
  // this modal was rendered from.
  useQuery({
    queryKey: ["APPLICATION", String(application.id)],
    queryFn: () => addressApi.getApplicationById(String(application.id)),
    enabled: isPending && !hasTimedOut,
    refetchInterval: PENDING_POLL_INTERVAL_MS
  });

  return { isPending, hasTimedOut };
};

export const AiSummary = ({ application, recommendation }: AiSummaryProps) => {
  const [isTurnOffOpened, setIsTurnOffOpened] = useState(false);
  const { update } = useAiRecommendationSummary();
  const { isPending, hasTimedOut } = usePendingSummaryPolling(application);

  const summary = application.recommendation_summary;

  // Nothing generated for this application, the summary explains a different
  // recommendation than the one shown, or generation never finished: skip the
  // card rather than block the rule breakdown below it.
  if (!summary) return null;
  if (summary.recommendation !== recommendation) return null;
  if (isPending && hasTimedOut) return null;

  return (
    <div className="px-6 pb-5">
      <Card className="ai-animated-border p-6 space-y-4">
        <div className="flex items-center gap-4">
          <IconAI bgSize={24} />
          <h2 className="text-base font-bold text-content-1000">AI summary</h2>
        </div>

        <hr className="border-alpha-100" />

        {isPending ? (
          <SummarySkeleton />
        ) : (
          <SummaryText key={summary.id} summary={summary} />
        )}

        <Show if={!isPending}>
          <AiSummaryFeedback
            applicationId={application.id}
            summary={summary}
            onTurnOff={() => setIsTurnOffOpened(true)}
          />
        </Show>
      </Card>

      <div className="mt-2.5 flex items-center justify-end gap-2 opacity-80">
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={16}
          className="text-content-300"
        />
        <span className="text-[13px] font-medium text-content-300">
          AI can make mistakes
        </span>
      </div>

      <TurnOffAiSummaryDialog
        opened={isTurnOffOpened}
        loading={update.isPending}
        onClose={() => setIsTurnOffOpened(false)}
        onConfirm={() =>
          update.mutate(
            { explanation_summary: false },
            { onSuccess: () => setIsTurnOffOpened(false) }
          )
        }
      />
    </div>
  );
};
